import {
  Button,
  Tooltip,
  SegmentedController,
  SegmentedControllerPanel,
} from '@paypalcorp/pp-react'
import { default as FileSaver } from 'file-saver'
import React, { FunctionComponent, useContext, useState } from 'react'
import { Accordion } from '../../../components/integration-builder/accordion'
import { CheckoutSimulatorContext } from '../../../components/integration-builder/context/CheckoutSimulatorContext'
import withAnalytics from '../../../utils/withAnalytics'
import styles from '../integration-builder.module.css'
import { zippedSamplesUrl } from '../../../lib/assetsUrl'
import { CodeSelectorOption } from '../../../components/integration-builder/code-selector/interfaces'
import { CheckoutSimulatorParametersProps } from '../../../components/integration-builder/context/interfaces'
import {
  scratchInstructions,
  cloneInstructions,
} from '../../../utils/Instructions'
import { removeGAParam } from '../../../components/pp-react-wrappers'

const Build: FunctionComponent = () => {
  const { integrationType, options, parameters } = useContext(
    CheckoutSimulatorContext,
  )

  const [selectedIndex, setSelectedIndex] = useState(0)
  const clickHandler = (tab: any) => {
    setSelectedIndex(tab.getAttribute('data-tab-index'))
  }

  const fptiData = typeof window !== `undefined` && window.fpti

  const frontendLanguageMap: { [x: string]: string } = {
    html: 'HTML',
    react: 'React',
    angular: 'Angular',
  }

  const backendLanguageMap: { [x: string]: string } = {
    java: 'Java',
    nodejs: 'NodeJS',
  }

  let selectedBackendLanguage =
    options?.find((option) => option.name === 'backend')?.value || 'nodejs'

  let selectedFrontendLanguage =
    options?.find((option) => option.name === 'frontend')?.value || 'html'

  const handleDownload = (
    option: CodeSelectorOption,
    parameters: CheckoutSimulatorParametersProps,
  ) => {
    // THIS IS NEEDED SINCE THE REPOSITORY CONTAINS NODE AND THE LOGIC IN CONTEXT IS PROPERLY CALLED NODEJS
    const languageBack =
      selectedBackendLanguage === 'nodejs' ? 'node' : selectedBackendLanguage
    let file = ''

    const repositoryMap: {
      [type: string]: { [back: string]: { [front: string]: string } }
    } = {
      standard: {
        node: {
          html: 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-Standard-Checkout-Sample/archive/refs/heads/main.zip',
          react:
            'https://github.com/paypaldev/PayPal-React-FullStack-Standard-Checkout-Sample/archive/refs/heads/main.zip',
        },
      },
    }
    if (integrationType === 'advanced' && option) {
      if (
        option?.value === 'html' &&
        parameters?.buttons.includes('payment-buttons_3ds')
      ) {
        file = `https://github.com/paypaldev/PayPal-JavaScript-FullStack-3Ds-Advanced-Checkout-Sample/archive/refs/heads/main.zip`
      } else if (option?.value === 'html') {
        file = `https://github.com/paypaldev/PayPal-JavaScript-FullStack-Advanced-Checkout-Sample/archive/refs/heads/main.zip`
      } else {
        file = `https://github.com/paypaldev/PayPal-React-FullStack-Advanced-Checkout-Sample/archive/refs/heads/main.zip`
      }
    } else {
      const typeMap = repositoryMap.standard
      const backendMap = typeMap[languageBack as keyof typeof typeMap]
      const filePath =
        backendMap[option.value as keyof typeof backendMap] ||
        `${zippedSamplesUrl}/standard/${option.value}-${languageBack}.zip`
      file = filePath
    }

    FileSaver.saveAs(file, `${file.split('/').at(-1)}`)

    // CAPTURE ANALYTICS
    if (fptiData && window.PAYPAL?.analytics?.instance) {
      const event = `download_${options[1].value}_${languageBack}_${
        integrationType === 'advanced' ? 'ac' : 'sc'
      }`
      window.PAYPAL.analytics.instance.record({
        data: {
          ...fptiData,
          component: 'devdiscoverynodeweb',
          space_key: 'SKEXJE',
          e: 'cl',
          link: event,
          event_action: event,
          page_name: fptiData && fptiData.page.replace(/:/g, '_'),
          event_name:
            fptiData && `${fptiData.page.replace(/:/g, '_')}_file_download`,
          pu: removeGAParam(window.location.href),
        },
      })
    }
  }

  /**
   * This function gets the GitHub Repo link
   * @param options
   * @param parameters
   * @returns url
   */
  const getGitHubLink = (
    options: CodeSelectorOption[] = [],
    parameters: CheckoutSimulatorParametersProps,
  ): string => {
    const optionValue = options[1]?.value
    if (
      optionValue === 'html' &&
      parameters?.buttons.includes('payment-buttons_3ds')
    ) {
      return 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-3Ds-Advanced-Checkout-Sample'
    } else if (optionValue === 'html') {
      return 'https://github.com/paypaldev/PayPal-JavaScript-FullStack-Advanced-Checkout-Sample'
    }

    return 'https://github.com/paypaldev/PayPal-React-FullStack-Advanced-Checkout-Sample'
  }

  /**
   * Selects the instructions based on the configuration and the parameters
   * @param options
   * @param parameters
   * @param setupInstructions
   * @returns
   */
  const getSetupInstructions = (
    options: CodeSelectorOption[] = [],
    parameters: CheckoutSimulatorParametersProps = {},
    setupInstructions,
  ) => {
    // Short circuit if needed
    if (!Array.isArray(setupInstructions)) {
      console.error('setupInstructions should be an array.')
      return
    }

    const instructions = setupInstructions.find((instruction) => {
      const optionValue = options[0]?.value
      const languageValue = options[1]?.value
      const { paymentModel } = parameters

      if (instruction.type !== optionValue) {
        return false
      }

      if (optionValue === 'standard') {
        return (
          languageValue === instruction.language &&
          ['recurring', 'onetime-payment'].includes(instruction.paymentModel) &&
          instruction.paymentModel === paymentModel
        )
      } else if (optionValue === 'advanced') {
        if (instruction.threeDs === 'payment-buttons_3ds') {
          return true
        }
        if (instruction.language === 'html') {
          return !parameters?.buttons?.includes('payment-buttons_3ds')
        }
        if (instruction.language === 'react') {
          return true
        }
      }

      return false
    })

    return instructions?.instructions
  }

  return (
    <div className={styles.checkout_simulator__accordion_container}>
      <Accordion
        number={2}
        color="secondary"
        heading="Setup Your Code"
        defaultCollapsed
        content={
          <>
            <div className={styles.checkout_simulator__subtitle_wrapper}>
              <p className={styles.checkout_simulator__fragment_subtitle}>
                Build your application and test in the sandbox
              </p>
              <Tooltip
                name="tooltip1"
                description="Test your application with transactions in the PayPal sandbox."
                windowId="window1"
                imagePosition="bottom-left"
              />
            </div>
            <SegmentedController
              name="TabsGroup1"
              tabs={[
                {
                  label: 'Start From Scratch',
                  id: 'group1-tab1',
                  panelId: 'group1-panel1',
                },
                {
                  label: 'Sample Repo',
                  id: 'group1-tab2',
                  panelId: 'group1-panel2',
                },
              ]}
              selectedTabIdx={selectedIndex}
              onClick={(event) => clickHandler(event.target)}
            >
              <SegmentedControllerPanel
                id="group1-panel1"
                tabId="group1-tab1"
                className="pp_react_doc__pass-your-own-class"
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: getSetupInstructions(
                      options,
                      parameters,
                      scratchInstructions,
                    ),
                  }}
                />
              </SegmentedControllerPanel>
              <SegmentedControllerPanel
                id="group1-panel2"
                tabId="group1-tab2"
                className="pp_react_doc__pass-your-own-class"
              >
                <div
                  dangerouslySetInnerHTML={{
                    __html: getSetupInstructions(
                      options,
                      parameters,
                      cloneInstructions,
                    ),
                  }}
                />
              </SegmentedControllerPanel>
            </SegmentedController>
            <Button
              id="download"
              className={styles.checkout_simulator__download_button}
              title="download"
              secondary
              onClick={() => handleDownload(options[1], parameters)}
            >
              Download
            </Button>
          </>
        }
      />
    </div>
  )
}

export default withAnalytics(Build)
